<template>
  <section
    class="ls-cover"
    :class="[`ls-cover--n${shown.length}`, { 'ls-cover--type': !shown.length }]"
    aria-labelledby="ls-cover-title"
  >
    <div
      v-if="shown.length"
      class="ls-cover__frames"
    >
      <figure
        v-for="(image, i) in shown"
        :key="image.url"
        class="ls-cover__frame"
      >
        <img
          :src="image.url"
          :alt="image.alt || ''"
          :class="{ 'is-contain': image.fit === 'contain' }"
          :loading="i === 0 ? 'eager' : 'lazy'"
          :fetchpriority="i === 0 ? 'high' : null"
          decoding="async"
          @error="drop(image.url)"
        >
        <figcaption
          v-if="image.credit"
          class="ls-cover__credit"
        >{{ image.credit }}</figcaption>
      </figure>
    </div>
    <div class="ls-cover__text">
      <h1
        id="ls-cover-title"
        class="ls-cover__headline"
      >{{ headline }}</h1>
      <p
        v-if="meta"
        class="ls-cover__meta"
      >{{ meta }}</p>
    </div>
  </section>
</template>

<script>
const MAX_FRAMES = 3

export default {
  name: 'LsCover',
  props: {
    images: { type: Array, default: () => [] },
    headline: { type: String, required: true },
    collectionName: { type: String, default: '' },
    styleCount: { type: Number, default: 0 },
  },
  data() {
    return { failed: [] }
  },
  computed: {
    shown() {
      return this.images.filter(img => !this.failed.includes(img.url)).slice(0, MAX_FRAMES)
    },
    meta() {
      const parts = []
      const name = this.collectionName.trim()
      if (name && name.toLowerCase() !== this.headline.trim().toLowerCase()) parts.push(name)
      if (this.styleCount > 0) parts.push(`${this.styleCount} ${this.styleCount === 1 ? 'style' : 'styles'}`)
      return parts.join('  /  ')
    },
  },
  methods: {
    drop(url) {
      this.failed = [...this.failed, url]
    },
  },
}
</script>

<style lang="scss" scoped>
@import './ls-base';

.ls-cover {
  position: relative;
  // On phones the sticky order bar (about 72px) sits over the bottom of the
  // first screen, so the cover leaves room for it.
  min-height: min(calc(88svh - 72px), 760px);
  display: grid;
  background: var(--ls-surface);
  overflow: hidden;

  &__frames {
    grid-area: 1 / 1;
    display: grid;
    grid-template-columns: 1fr;
    min-height: inherit;

    @include ls-up($ls-lg) {
      gap: 4px;
    }
  }

  // Mobile shows the first photograph only; desktop splits 5/3/3.
  &__frame {
    position: relative;
    margin: 0;
    min-height: inherit;
    background: var(--ls-surface-2);

    &:not(:first-child) {
      display: none;
    }

    img {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center 30%;
      animation: ls-fade 0.2s $ls-ease both;

      &.is-contain {
        object-fit: contain;
        padding: 6%;
      }
    }
  }

  @include ls-up($ls-lg) {
    &--n2 &__frames {
      grid-template-columns: 5fr 3fr;
    }

    &--n3 &__frames {
      grid-template-columns: 5fr 3fr 3fr;
    }

    &__frame:not(:first-child) {
      display: block;
    }
  }

  &__credit {
    position: absolute;
    right: 12px;
    bottom: 8px;
    z-index: 2;
    color: #fff;
    font: 0.6875rem/1.2 var(--ls-body-font);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  }

  // Text sits over a scrim strong enough to keep white text at AA on any
  // photograph: the bottom third is at least 72% black.
  &__text {
    grid-area: 1 / 1;
    align-self: end;
    position: relative;
    z-index: 1;
    padding: 96px $ls-gutter clamp(28px, 6vw, 64px);
    color: #fff;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.74) 0%, rgba(0, 0, 0, 0.5) 45%, rgba(0, 0, 0, 0) 100%);

    @include ls-up($ls-lg) {
      padding-top: 160px;
      background: linear-gradient(to top, rgba(0, 0, 0, 0.72) 0%, rgba(0, 0, 0, 0.42) 50%, rgba(0, 0, 0, 0) 100%);
    }
  }

  &__headline {
    @include ls-heading;
    margin: 0;
    max-width: 18ch;
    color: inherit;
    font-size: clamp(1.875rem, 7.6vw, 4.5rem);
    overflow-wrap: anywhere;

    @include ls-up($ls-lg) {
      font-size: clamp(2.75rem, 4.6vw, 4.5rem);
    }
  }

  // A split cover keeps the headline inside the first photograph, so it never
  // runs across the gap between frames (the cover is full-bleed: 100vw).
  @include ls-up($ls-lg) {
    &--n2 &__headline {
      max-width: min(18ch, calc(100vw * 5 / 8 - #{$ls-gutter} - 32px));
    }

    &--n3 &__headline {
      max-width: min(18ch, calc(100vw * 5 / 11 - #{$ls-gutter} - 32px));
    }
  }

  &__meta {
    margin: 18px 0 0;
    @include ls-label;
    font-weight: 500;
    color: inherit;
  }

  // No photographs: the cover is typography on the brand's surface.
  @include ls-up($ls-lg) {
    min-height: min(88svh, 760px);
  }

  &--type {
    min-height: min(56svh, 520px);

    .ls-cover__text {
      max-width: none;
      align-self: center;
      color: var(--ls-ink);
      background: none;
      padding-top: 64px;
    }

    .ls-cover__headline {
      max-width: 18ch;
      font-size: clamp(2.5rem, 8vw, 6rem);
    }

    .ls-cover__meta {
      color: var(--ls-muted);
    }
  }
}

@keyframes ls-fade {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ls-cover__frame img {
    animation: none;
  }
}
</style>
